/**
 * <s-product-reviews> — opens Judge.me's native review form from the theme's own "Write a review" button.
 *
 * Required markup:
 *   button[data-write-review]      — theme-styled trigger.
 *   .jdgm-review-widget            — container the Judge.me Core app embed renders into.
 *
 * Judge.me renders its widget asynchronously, so the trigger waits briefly for the form link
 * (.jdgm-write-rev-link) to appear; until then it simply scrolls to the widget.
 */
class SProductReviews extends HTMLElement {
  #ac;
  #observer;
  #timeoutId = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stopWaiting();
  }

  get #widget() {
    return this.querySelector('.jdgm-review-widget');
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const more = event.target.closest('[data-reviews-more]');
    if (more instanceof HTMLElement) {
      this.#toggleMore(more);
      return;
    }

    if (!event.target.closest('[data-write-review]')) return;

    const widget = this.#widget;
    if (!widget) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    widget.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

    if (this.#openForm(widget)) return;

    // Widget still loading: open the form as soon as Judge.me inserts it (give up after 5s).
    this.#stopWaiting();
    this.#observer = new MutationObserver(() => {
      if (this.#openForm(widget)) this.#stopWaiting();
    });
    this.#observer.observe(widget, { childList: true, subtree: true });
    this.#timeoutId = window.setTimeout(() => this.#stopWaiting(), 5000);
  };

  /** Reveal or collapse reviews beyond the first few; focus moves to the first newly shown review. */
  #toggleMore(button) {
    const extras = Array.from(this.querySelectorAll('[data-review-extra]'));
    if (extras.length === 0) return;

    const expand = button.getAttribute('aria-expanded') !== 'true';
    extras.forEach((item) => {
      item.hidden = !expand;
    });
    button.setAttribute('aria-expanded', String(expand));
    button.textContent = expand ? button.dataset.labelLess || '' : button.dataset.labelMore || '';

    if (expand) {
      const first = extras[0];
      first.tabIndex = -1;
      first.focus({ preventScroll: true });
    }
  }

  #openForm(widget) {
    const link = widget.querySelector('.jdgm-write-rev-link');
    if (!(link instanceof HTMLElement)) return false;

    const form = widget.querySelector('.jdgm-form-wrapper');
    const isOpen = form instanceof HTMLElement && form.offsetParent !== null;
    if (!isOpen) link.click();

    const firstField = widget.querySelector('.jdgm-form input, .jdgm-form textarea');
    if (firstField instanceof HTMLElement) {
      window.setTimeout(() => firstField.focus({ preventScroll: true }), 300);
    }
    return true;
  }

  #stopWaiting() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    window.clearTimeout(this.#timeoutId);
  }
}

if (!customElements.get('s-product-reviews')) {
  customElements.define('s-product-reviews', SProductReviews);
}
